import { ChevronDownIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import cscLogo from "../../assets/images/insignary-clarity-sc-lockup-dark.svg";
import LanguageSelector from "../UI/LanguageSelector";
import UserAvatar from "../UI/UserAvatar";
import AboutModal from "../UI/AboutModal";
import { PermissionContext } from "@contexts/PermissionContext";
import { LOCAL_STORAGE } from "@constants/index";
import { getLocalStorage } from "@utils/local-storage";
import { logout } from "@utils/auth";
import useSystemInfo from "../../hooks/useSystemInfo";
import AutoUpdateModal from "../UI/AutoUpdateModal";
import ToastConstant from "@configs/react-toastify/snackbar";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";

const Header = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isAutoUpdateOpen, setIsAutoUpdateOpen] = useState(false);
  const navigate = useNavigate();
  const { permissions, clearPermissions, updatePermissions } = useContext(PermissionContext);
  const { systemInfo, loading, error } = useSystemInfo();
  const [roleName, setRoleName] = useState("");
  const [userName, setUserName] = useState(null);

  // Get user info from localStorage
  const userId = getLocalStorage(LOCAL_STORAGE.USER_ID);

  useEffect(() => {
    if (permissions.includes("SUPERUSER")) {
      setRoleName("Superuser");
    } else if (userId) {
      getUserDetails.mutate();
    }
  }, [])

  // Get User Details API
  const getUserDetails = useMutationData(
    queryKeys.getMyProfile,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setRoleName(response.roleCdName);
          setUserName(response?.userName || "User");
          if (response?.permissions) {
            updatePermissions(response.permissions);
          }
        }
      },
      onError: (error) => {
        console.error("Error during get user details:", error);
        ToastConstant(t("user.get-details-error", "Failed to get user details"), TOAST.error);
      },
    },
  );

  const handleLogout = async () => {
    try {
      // Close dropdown first
      setIsProfileDropdownOpen(false);

      // Call logout function which handles API call and toast
      await logout(false, { clearPermissions, clearIndexedDb: true });

      // Redirect to login
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error in header:", error);
    }
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const toggleAutoUpdate = () => {
    setIsAutoUpdateOpen(!isAutoUpdateOpen);
    setIsProfileDropdownOpen(false);
  };

  return (
    <header className={`rounded-b-2xl border-b ${colors.headerBorder} ${colors.headerBackground} px-6 py-3`}>
      <div className="flex items-center justify-between">
        {/* Left side - Logo and Breadcrumb */}
        <div className="flex items-center space-x-6">
          {/* CSC Logo */}
          <div className="flex items-center">
            <img 
              src={cscLogo} 
              alt="Insignary CLARITY SC" 
              className="h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={() => navigate("/dashboard")}
            />
          </div>
        </div>

        {/* Right side - Language toggle, notifications, and profile */}
        <div className="flex items-center">
          {/* Language Selector */}
          <LanguageSelector />

          {/* Vertical Divider */}
          <div className={`h-6 w-px ${colors.headerDivider}`}></div>

          {/* Profile Section with Dropdown */}
          <div className="relative">
            <button
              id="user-menu-button"
              onClick={toggleProfileDropdown}
              className={`flex items-center space-x-3 px-4 py-2 rounded-lg ${colors.headerHover} transition-colors duration-200`}
              aria-expanded={isProfileDropdownOpen}
              aria-haspopup="true"
            >
              <UserAvatar
                size="md"
                fallbackText={userId || "U"}
              />
              <div className="text-right">
                <div className={`text-sm font-medium ${colors.headerText}`}>{userName || userId || "User"}</div>
                <div 
                  className={`text-xs ${colors.headerMuted} cursor-help truncate max-w-32`}
                  data-tooltip-id="role-tooltip"
                  data-tooltip-content={roleName}
                >
                  {roleName}
                </div>
              </div>
              <ChevronDownIcon
                className={`h-4 w-4 ${colors.headerMuted} transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {/* Dropdown Menu */}
            <div
              className={`absolute right-0 mt-2 w-60 ${colors.headerBackground} rounded-xl shadow-2xl py-3 z-50 border ${colors.headerDividerBorder} transition-all duration-300 ease-out transform ${isProfileDropdownOpen
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                }`}
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="user-menu-button"
            >
              {/* Profile Info */}
              <div className={`px-4 py-4 border-b ${colors.headerDividerBorder}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <UserAvatar
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                      alt="User Avatar"
                      size="md"
                      fallbackText={userId || "U"}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-base font-semibold ${colors.headerText} truncate`}>{userName || userId || "User"}</div>
                    <div 
                      className={`text-sm ${colors.headerMuted} leading-tight cursor-help truncate w-36`}
                      data-tooltip-id="role-tooltip"
                      data-tooltip-content={roleName}
                    >
                      {roleName}
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div>
                <button
                  onClick={() => {
                    setIsProfileDropdownOpen(false);
                    navigate("/profile");
                    // Add profile page navigation here if needed
                  }}
                  className={`flex items-center w-full px-3 py-3 text-sm ${colors.headerMenuText} ${colors.headerHover} transition-all duration-200 group`}
                >
                  <UserCircleIcon className={`h-4 w-4 mr-3 ${colors.headerIcon}`} />
                  {t("common.profile-settings")}
                </button>

                <button
                  onClick={() => {
                    setIsProfileDropdownOpen(false);
                    setIsAboutModalOpen(true);
                  }}
                  className={`flex items-center w-full px-3 py-3 text-sm ${colors.headerMenuText} ${colors.headerHover} transition-all duration-200 group`}
                >
                  <svg className={`h-4 w-4 mr-3 ${colors.headerIcon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t("common.about")}
                </button>

                {/* Auto Update Button - Only visible for SUPERUSER */}
                {permissions.includes("SUPERUSER") && (
                  <button
                    onClick={toggleAutoUpdate}
                    className={`flex items-center w-full px-3 py-3 text-sm ${colors.headerMenuText} ${colors.headerHover} transition-all duration-200 group`}
                  >
                    <svg className={`h-4 w-4 mr-3 ${colors.headerIcon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t("common.system-auto-update")}
                  </button>
                )}
              </div>

              {/* Divider */}
              <div className={`border-t ${colors.headerDividerBorder} my-2`}></div>

              {/* Logout */}
              <div>
                <button
                  onClick={handleLogout}
                  className={`flex items-center w-full px-3 py-3 text-sm ${colors.headerDangerText} ${colors.headerDangerHover} transition-all duration-200 group`}
                >
                  <div className="flex items-center justify-center w-5 h-5 mr-4">
                    <svg className={`h-4 w-4 ${colors.headerDangerText} ${colors.headerDangerHoverText} transition-colors duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  {t("common.sign-out")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop to close dropdown when clicking outside */}
      {isProfileDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsProfileDropdownOpen(false)}
        />
      )}

      {/* About Modal */}
      <AboutModal
        isOpen={isAboutModalOpen}
        onClose={() => setIsAboutModalOpen(false)}
        systemInfo={systemInfo}
        loading={loading}
        error={error}
      />

      {/* Auto Update Modal */}
      <AutoUpdateModal
        isOpen={isAutoUpdateOpen}
        onClose={() => setIsAutoUpdateOpen(false)}
      />

      {/* Role Name Tooltip */}
      <Tooltip
        id="role-tooltip"
        place="bottom"
        className="!text-xs !rounded-lg !px-3 !py-2 !max-w-[300px] !min-w-[200px] !z-50"
        style={{
          backgroundColor: colors.headerTooltipBackgroundColor,
          color: colors.headerTooltipTextColor,
          fontSize: '12px',
          borderRadius: '8px',
          padding: '8px 12px',
          maxWidth: '300px',
          minWidth: '200px',
          zIndex: 50
        }}
      />
    </header>
  );
};

export default Header;
